/** Mock DTE timeline for the Options Income & Hedging tab.
 *
 * Visual contract only. Mirrors the existing Workbench timeline (Today,
 * expiry ticks, chips, selected DTE, earnings marker, policy window) and
 * performs no network access.
 */
import { StatusBadge } from "@/components/wheel/StatusBadge";
import { CachedBadge, EarningsQualityBadge, MockBadge, WorkspacePanel, useWorkspaceAdmin } from "@/workspace/primitives";
import {
  MOCK_AS_OF, POLICY_WINDOW, availableExpirations, dteBand, earningsConflict,
} from "@/workspace/mockTickerData";

const BAND = {
  short: { label: "Short", color: "#93C5FD", tip: "Shorter than the 35–45 DTE policy window." },
  target: { label: "Target", color: "#E4E4E7", tip: "Inside the 35–45 DTE policy window." },
  long: { label: "Long", color: "#F97316", tip: "Longer than the 35–45 DTE policy window." },
};

const CONFLICT_TIER = {
  clear: "info",
  inside: "caution",
  predicted: "caution",
  unavailable: "caution",
};

function fmtChip(date) {
  const d = new Date(`${date}T12:00:00Z`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function fmtFull(date) {
  const d = new Date(`${date}T12:00:00Z`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

export function DteTimeline({ ticker, selectedExpiry, onSelectExpiry }) {
  const isAdmin = useWorkspaceAdmin();
  const expiries = ticker.live
    ? (ticker.availableExpirations || [])
    : availableExpirations();
  const selected = expiries.find((e) => e.date === selectedExpiry) || expiries[0];
  const conflict = earningsConflict(ticker.earnings, selected?.dte);
  const maxDte = Math.max(7, POLICY_WINDOW.max, ticker.earnings?.daysTo || 0, ...expiries.map((e) => e.dte));
  const pct = (d) => Math.min(Math.max((d / maxDte) * 100, 2), 98);
  const showEarningsMark = Boolean(conflict.date && Number.isFinite(conflict.daysTo) && conflict.daysTo > 0 && conflict.daysTo <= maxDte);
  const predictedMark = conflict.quality === "predicted";
  const band = selected ? BAND[dteBand(selected.dte)] : null;

  return (
    <WorkspacePanel
      title="DTE timeline"
      subtitle={ticker.live
        ? "Requested analysis tenor · Canonical 30-DTE historical simulation"
        : `As of ${MOCK_AS_OF} · ${POLICY_WINDOW.label}`}
      notice={ticker.live ? <CachedBadge /> : <MockBadge />}
      hint={ticker.live
        ? "Listed expirations come from the current cached ticker payload. Fetch time is not market observation time."
        : "Listed Fridays are fixtures. The band is the existing Wheel 35–45 DTE policy window. Earnings uses the header date and quality only — a predicted date is never treated as confirmed."}
      testid="options-dte-timeline"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <div className="text-[11px] font-mono-data" data-testid="dte-to-expiry">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>DTE to expiry</span>
          {" "}
          <span className="font-bold">{selected ? `${selected.dte}d` : "—"}</span>
          {selected ? (
            <span style={{ color: "var(--hoip-text-tertiary)" }}> · {fmtFull(selected.date)}</span>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {band ? (
            <StatusBadge tier={band.label === "Long" ? "caution" : "info"} text={band.label} title={band.tip} />
          ) : null}
          <StatusBadge
            tier={CONFLICT_TIER[conflict.state]}
            text={conflict.label}
            title={conflict.date ? `${conflict.label} · ${conflict.date}` : conflict.label}
            testid="dte-earnings-conflict"
          />
          {conflict.quality ? <EarningsQualityBadge quality={conflict.quality} testid="dte-earnings-quality" /> : null}
        </div>
      </div>

      <div className="flex justify-between text-[10px] mb-1" style={{ color: "var(--hoip-text-tertiary)" }}>
        <span>Today</span>
        <span>{maxDte}d</span>
      </div>

      <div
        className="relative h-11 rounded-sm min-w-0"
        style={{ background: "var(--hoip-bg)", border: "1px solid var(--hoip-border)" }}
        data-testid="dte-track"
      >
        <div
          className="absolute top-1 bottom-1"
          title={POLICY_WINDOW.label}
          style={{
            left: `${pct(POLICY_WINDOW.min)}%`,
            width: `${pct(POLICY_WINDOW.max) - pct(POLICY_WINDOW.min)}%`,
            background: "rgba(228,228,231,0.10)",
            borderLeft: "1px dashed rgba(228,228,231,0.35)",
            borderRight: "1px dashed rgba(228,228,231,0.35)",
          }}
          data-testid="dte-policy-band"
        />
        {showEarningsMark ? (
          <div
            className="absolute top-0 bottom-0 z-10 pointer-events-none"
            style={{ left: `${pct(conflict.daysTo)}%` }}
            data-testid="dte-earnings-mark"
            title={`${predictedMark ? "Predicted" : conflict.quality} earnings · ${conflict.date}`}
          >
            <div
              className="absolute top-0 bottom-0"
              style={{
                width: 2,
                marginLeft: -1,
                background: predictedMark ? "transparent" : "#EF4444",
                borderLeft: predictedMark ? "2px dashed #D97706" : "none",
              }}
            />
            <div
              className="absolute -top-0.5 -left-2 text-[8px] font-mono-data whitespace-nowrap"
              style={{ color: predictedMark ? "#D97706" : "#F87171" }}
            >
              E
            </div>
          </div>
        ) : null}

        {expiries.map((e) => {
          const active = e.date === selectedExpiry;
          const info = BAND[e.band] || BAND[dteBand(e.dte)] || BAND.target;
          return (
            <button
              key={e.date}
              type="button"
              title={`${e.label} · ${info.label}`}
              onClick={() => onSelectExpiry(e.date)}
              className="absolute top-2 flex flex-col items-center -translate-x-1/2 cursor-pointer"
              style={{ left: `${pct(e.dte)}%` }}
              data-testid={`dte-tick-${e.date}`}
            >
              <span
                className="block rounded-full"
                style={{
                  width: active ? 10 : 7,
                  height: active ? 10 : 7,
                  background: active ? "#3B82F6" : info.color,
                  boxShadow: active ? "0 0 0 2px rgba(59,130,246,0.35)" : "none",
                }}
              />
            </button>
          );
        })}
      </div>

      <div className="flex gap-1.5 overflow-x-auto min-w-0 mt-2 pb-1" data-testid="expiry-chip-row">
        {expiries.map((e) => {
          const active = e.date === selectedExpiry;
          const info = BAND[e.band] || BAND[dteBand(e.dte)] || BAND.target;
          return (
            <button
              key={e.date}
              type="button"
              onClick={() => onSelectExpiry(e.date)}
              title={info.tip}
              data-testid={`expiry-chip-${e.date}`}
              className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-mono-data border rounded-sm shrink-0"
              style={{
                color: active ? "#3B82F6" : "var(--hoip-text-secondary)",
                borderColor: active ? "#3B82F6" : "var(--hoip-border)",
                background: active ? "rgba(59,130,246,0.08)" : "var(--hoip-card)",
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: info.color }} />
              <span className={active ? "font-bold" : ""}>{fmtChip(e.date)}</span>
              <span style={{ color: "var(--hoip-text-tertiary)" }}>{e.dte}d</span>
              <span className="text-[8px] uppercase" style={{ color: info.color }}>{info.label}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        <span>Selected: <span className="font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>{selected ? `${fmtFull(selected.date)} · ${selected.dte}d to expiry` : "—"}</span></span>
        {showEarningsMark ? (
          <span>
            Earnings marker: <span className="font-mono-data">{conflict.date}</span>
            {predictedMark ? " · predicted, not confirmed" : ` · ${conflict.quality}`}
          </span>
        ) : (
          <span>No earnings marker — date is unavailable or outside this horizon.</span>
        )}
        {selected?.beyondP75 || (ticker.regime.sojournP75 != null && selected && selected.dte > ticker.regime.sojournP75) ? (
          <span>Beyond regime P75 sojourn ({ticker.regime.sojournP75}d).</span>
        ) : null}
      </div>

      {ticker.live ? (
        <div className="mt-2 pt-2 border-t space-y-0.5" style={{ borderColor: "var(--hoip-divider)" }} data-testid="dte-tenor-meaning">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px]">
            <div>
              <span style={{ color: "var(--hoip-text-secondary)" }}>Requested analysis tenor</span>
              {" "}
              <span className="font-mono-data">{selected ? `${fmtFull(selected.date)} · ${selected.dte}d` : "expiry not in query"}</span>
            </div>
            <div>
              <span style={{ color: "var(--hoip-text-secondary)" }}>Historical research model</span>
              {" "}
              <span className="font-mono-data">Canonical 30-DTE simulation</span>
            </div>
          </div>
          <p className="text-[10px] mt-1 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
            URL expiry/DTE is a page tenor only. Historical research is a Canonical 30-DTE simulation and is not based on the selected expiry. This timeline does not select a current option contract.
          </p>
        </div>
      ) : (
        <p className="text-[10px] mt-2 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>
          {isAdmin
            ? "Policy window copies the existing Wheel 35–45 DTE band. Earnings quality is the header field (confirmed / expected / predicted / unavailable). No chain, no earnings calendar endpoint."
            : "Illustrative data. Data may be delayed."}
        </p>
      )}
    </WorkspacePanel>
  );
}
